<script setup lang="ts">
import { ref, computed } from "vue";
import { storeToRefs } from "pinia";

import { configStore } from "@/stores/config";

import { useComponentErrorCapture } from "@/mixins/error";
import { getCurrentLangcode } from "@/mixins/translatedProperty";
import { formatDate } from "@/mixins/date";

const configStorage = configStore();

useComponentErrorCapture();

const props = defineProps({
  range: {
    type: Boolean,
    default: false
  },
	minDate: {
    type: [Date, String],
    default: null
  },
	maxDate: {
    type: [Date, String],
    default: null
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["change"]);
const { config } = storeToRefs(configStorage);

const date = ref<Date|Date[]|null>(null);
const tempDate = ref<Date|Date[]>([]);
const activeDP = ref(false);

const timeFormat24h = computed(() => {
  return config.value?.localization?.time_format_24h ?? true;
});

function clearDate() {
	if (props.range === true) {
  	date.value = [];
	} else {
		date.value = null;
	}
	emitDate();
}

function handleInternalChange(newValue: Date|Date[]) {
  tempDate.value = newValue;
}

function handleClosed() {activeDP
	activeDP.value = false;
  date.value = tempDate.value;
	emitDate();
}

function emitDate() {
	emit("change", date.value);
}

</script>
<template>
	<div class="datepicker">
		<Teleport to="body">
			<Transition name="opacity-fast">
				<div v-show="activeDP" class="datepicker__background"></div>
			</Transition>
		</Teleport>
		<Transition name="opacity-fast">
			<VueDatePicker
				v-model="date"
				:range="range"
				:teleport="true"
				:offset="10"
				:min-date="minDate"
				:max-date="maxDate"
				:locale="getCurrentLangcode() || 'en'"
				:selectText="$t('ui.save')"
				:is-24="timeFormat24h"
        :disabled="disabled"
				dark 
				hide-input-icon
				time-picker-inline
				@internal-model-change="handleInternalChange"
				@open="activeDP = true"
				@closed="handleClosed">
				<template #trigger>
					<button :disabled="disabled" class="datepicker__value">
						<template v-if="range === true && date && Array.isArray(date) && date.length > 0 && date[0]">
							{{ formatDate(date[0], timeFormat24h) }}
							<span
								v-if="date.length < 2"
								@click.stop="clearDate"
								class="datepicker__value__clear">
								<i class="fa-light fa-close"></i>
							</span>
						</template>
						<template v-else-if="date && (date instanceof Date)">
							{{ formatDate(date, timeFormat24h) }}						
							<span
								@click.stop="clearDate"
								class="datepicker__value__clear">
								<i class="fa-light fa-close"></i>
							</span>
						</template>
						<template v-else>{{ $t('ui.not_set') }}</template>
					</button>
					<template v-if="range === true">
						<div class="spacer">
							<i class="fa-light fa-right-arrow-alt"></i>
						</div>
						<button :disabled="disabled" class="datepicker__value">
							<template v-if="date && Array.isArray(date) && date.length > 1 && date[1]">
								{{ formatDate(date[1], timeFormat24h) }}
								<span
									@click.stop="clearDate"
									class="datepicker__value__clear">
									<i class="fa-light fa-close"></i>
								</span>
							</template>
							<template v-else>{{ $t('ui.not_set') }}</template>
						</button>
					</template>
				</template>
			</VueDatePicker>
		</Transition>
	</div>
</template>